import {useCallback, useRef, useState} from "react";
import {Typography} from "@mui/material";
import {loqate} from "../services/loqateService";

export const highlightMatch = (text, query) => {
    if (!query || !text) return text;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
        regex.test(part) ? (
            <Typography component="span" key={index} sx={{fontWeight: 700}}>
                {part}
            </Typography>
        ) : part,
    );
};

export const useAddressFunctions = (country, setValue) => {
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [open, setOpen] = useState(false);
    const autocompleteRef = useRef(null);

    const handleAddressSearch = useCallback(async (searchText, containerId) => {
        if (!searchText) {
            setSuggestions([]);
            return;
        }

        setIsSearching(true);
        try {
            const result = await loqate.capture.find({
                text: searchText,
                countries: country ?? undefined,
                container: containerId,
                limit: 8,
            });
            setSuggestions(result.items || []);
            setOpen(true);
        } catch (error) {
            setSuggestions([]);
        } finally {
            setIsSearching(false);
        }
    }, [country]);

    const populateAddressFields = useCallback((address)=> {
        if (address.line1) {
            setValue("addressLine1", address.line1, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }

        if (address.line2) {
            setValue("addressLine2", address.line2, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }

        if (address.city) {
            setValue("city", address.city, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }

        const postalCode = address.postalCode || "0000";
        setValue("code", postalCode, {
            shouldValidate: true,
            shouldDirty: true,
        });

        if (address.provinceName) {
            setValue("area", address.provinceName, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }

        setSuggestions([]);
        setOpen(false);
    }, [setValue]);

    const handleAddressSelect = useCallback(async (suggestion) => {
        if (!suggestion?.id) return;

        if (suggestion.type === "Container") {
            setSearchQuery(suggestion.text);
            await handleAddressSearch(suggestion.text, suggestion.id);
            return;
        }

        try {
            const result = await loqate.capture.retrieve({
                id: suggestion.id,
            });

            const address = result.items?.[0];
            if (!address) return;

            populateAddressFields(address);
        } catch (error) {
            console.error("Address retrieve failed:", error);
        }
    }, [handleAddressSearch, populateAddressFields]);

    return {
        suggestions,
        isSearching,
        searchQuery,
        setSearchQuery,
        open,
        setOpen,
        autocompleteRef,
        handleAddressSearch,
        populateAddressFields,
        handleAddressSelect,
    };
};
